import {
  useEventdetectorApi,
  useFetchAllEventdetectorDefinitions,
} from '@ais-ui/api';
import { useToast } from '@ais-ui/ui-core';
import { useEffect, useMemo, useRef } from 'react';
import { Session } from '@ais-ui/auth';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../Settings/SettingsProvider';
import { useNotificationSound } from '../utils/useNotificationSound';

interface EventDetail {
  event_ids: string[];
}

function severityToToastVariant(severity: string) {
  switch (severity) {
    case 'alert':
      return 'destructive';
    case 'warning':
      return 'warning';
    default:
      return 'default';
  }
}

function mapBackendSeverityToSeverity(
  severity: string | undefined,
): 'note' | 'warning' | 'alert' {
  const lowerSeverity = severity?.toLowerCase();

  switch (lowerSeverity) {
    case 'wdesev_note':
    case 'note':
      return 'note';
    case 'wdesev_warning':
    case 'warning':
      return 'warning';
    case 'wdesev_alert':
    case 'alert':
      return 'alert';
    default:
      return 'note';
  }
}

export const useEventNotification = (olSession: Session) => {
  const eventDetectorApi = useEventdetectorApi();
  const permissions = usePermissions();
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { playNotificationSound } = useNotificationSound();

  const soundAlertLevels = useMemo(() => {
    return {
      note:
        settings['ribbonPlugin.settings']?.sound_alert_levels?.note ?? false,
      warning:
        settings['ribbonPlugin.settings']?.sound_alert_levels?.warning ?? false,
      alert:
        settings['ribbonPlugin.settings']?.sound_alert_levels?.alert ?? false,
    };
  }, [settings]);

  const { data: detectors } = useFetchAllEventdetectorDefinitions(
    permissions.events,
  );
  const { toast } = useToast();

  // Use a ref to track failed event IDs across renders
  const failedEventIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleEventNotification = async (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;

      for (const eventId of customEvent.detail.event_ids) {
        if (!eventId) {
          console.error('Event ID of event notification is null or undefined');
          continue;
        }

        // Skip processing if we've already failed on this event ID
        if (failedEventIdsRef.current.has(eventId)) {
          continue;
        }

        if (!detectors || !detectors.records) continue;

        try {
          // Fetch the event using the eventid from the eventNotification
          const response = await eventDetectorApi.getEventDetails({
            eventId: eventId,
          });

          const fetchedEvent = response.records?.[0];

          if (!fetchedEvent) {
            console.warn(`No event details found for event ID: ${eventId}`);
            continue;
          }

          // Get the id of the detector that triggered the event
          const detectorId = fetchedEvent.definition?.id;
          if (!detectorId) {
            console.warn(`No detector ID found for event ID: ${eventId}`);
            continue;
          }

          // Go through all detectors of the user to find the detector with the right id
          const detector = detectors.records.find(
            (det) => det.id === detectorId,
          );

          if (detector) {
            // Get event details for notification
            const backendSeverity: string | undefined = fetchedEvent.event
              ?.severity as string;

            const severity = mapBackendSeverityToSeverity(backendSeverity);

            const variant = severityToToastVariant(severity);
            const event = fetchedEvent.event;
            const ship = event?.ship;
            const drone = event?.drone;
            const shipName = drone
              ? 'Drone'
              : ship?.name || 'Unknown ship';

            const toastTitle = 'New event from ' + detector.name;
            const toastDescription = 'Triggered by ' + shipName;

            // Post notification
            toast({
              variant: variant,
              title: toastTitle,
              description: toastDescription,
            });

            // Play sound if enabled for the specific event severity level
            if (
              (severity === 'note' && soundAlertLevels.note) ||
              (severity === 'warning' && soundAlertLevels.warning) ||
              (severity === 'alert' && soundAlertLevels.alert)
            ) {
              await playNotificationSound();
            }
          }
        } catch (error) {
          // Handle API errors
          console.error(
            `Error fetching event details for event ID: ${eventId}`,
            error,
          );

          // Add this event ID to our set of failed events
          failedEventIdsRef.current.add(eventId);

          // Show error toast to user - only once per event ID
          toast({
            variant: 'destructive',
            title: messages.genericToastTitleError,
            description: `Could not retrieve details for event with ID ${eventId}.`,
          });

          continue;
        }
      }
    };

    window.addEventListener('eventNotification', handleEventNotification);
    return () => {
      window.removeEventListener('eventNotification', handleEventNotification);
    };
  }, [
    detectors,
    eventDetectorApi,
    soundAlertLevels,
    messages,
    olSession,
    toast,
    playNotificationSound,
  ]);
};
